import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  ArrowLeft,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Plus,
  FileText,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

/**
 * ReconciliationList - List and start bank reconciliations
 */
export default function ReconciliationList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentWorkspace } = useWorkspace();
  
  const [loading, setLoading] = useState(true);
  const [reconciliations, setReconciliations] = useState([]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!currentWorkspace) return;
      try {
        const reconResponse = await fetch(
          `${API_URL}/api/banking/reconciliations?workspace_id=${currentWorkspace.workspace_id}`
        );
        if (reconResponse.ok) {
          const data = await reconResponse.json();
          setReconciliations(data.reconciliations || []);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentWorkspace]);

  // Open reconciliation from URL param
  useEffect(() => {
    const reconId = searchParams.get('open');
    if (reconId) {
      navigate(`/banking/reconcile/${reconId}`);
    }
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  // Group reconciliations by bank
  const reconsByBank = reconciliations.reduce((acc, recon) => {
    if (!acc[recon.bank_id]) {
      acc[recon.bank_id] = [];
    }
    acc[recon.bank_id].push(recon);
    return acc;
  }, {});

  return (
    <div className="container mx-auto p-6 max-w-4xl" data-testid="reconciliation-list-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/banking')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Banking
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Bank Reconciliations</h1>
            <p className="text-sm text-muted-foreground">
              Reconcile your bank statements with your books
            </p>
          </div>
        </div>
        
        <Button onClick={() => navigate('/banking/reconcile/new')} data-testid="new-reconciliation-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Reconciliation
        </Button>
      </div>

      {/* In-Progress Reconciliations */}
      {reconciliations.some(r => r.status === 'in_progress') && (
        <Card className="mb-6 border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              In Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {reconciliations.filter(r => r.status === 'in_progress').map((recon) => (
                <div
                  key={recon.reconciliation_id}
                  className="flex items-center justify-between p-3 bg-white dark:bg-background rounded-lg border cursor-pointer hover:border-primary transition-colors"
                  onClick={() => navigate(`/banking/reconcile/${recon.reconciliation_id}`)}
                  data-testid={`recon-row-${recon.reconciliation_id}`}
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">
                        {recon.bank_name} {recon.bank_mask && `****${recon.bank_mask}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(recon.start_date), 'MMM d')} - {format(new Date(recon.end_date), 'MMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm">
                      {formatCurrency(recon.statement_balance)}
                    </span>
                    <Badge variant="outline" className="bg-amber-100 text-amber-700">
                      Continue
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completed Reconciliations by Bank */}
      {Object.keys(reconsByBank).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(reconsByBank).map(([bankId, bankRecons]) => {
            const completedRecons = bankRecons.filter(r => r.status !== 'in_progress');
            if (completedRecons.length === 0) return null;
            
            const bankName = completedRecons[0]?.bank_name || 'Unknown Bank';
            const bankMask = completedRecons[0]?.bank_mask;
            
            return (
              <Card key={bankId}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {bankName} {bankMask && `****${bankMask}`}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {completedRecons.map((recon) => (
                      <div
                        key={recon.reconciliation_id}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => navigate(`/banking/reconcile/${recon.reconciliation_id}`)}
                        data-testid={`recon-row-${recon.reconciliation_id}`}
                      >
                        <div className="flex items-center gap-3">
                          {recon.status === 'completed' ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <X className="w-4 h-4 text-red-500" />
                          )}
                          <div>
                            <p className="text-sm font-medium">
                              {format(new Date(recon.start_date), 'MMM d')} - {format(new Date(recon.end_date), 'MMM d, yyyy')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {recon.status === 'completed' 
                                ? `Completed ${format(new Date(recon.completed_at), 'MMM d, yyyy')}`
                                : `Voided ${format(new Date(recon.voided_at), 'MMM d, yyyy')}`
                              }
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-sm">
                            {formatCurrency(recon.statement_balance)}
                          </span>
                          {recon.status === 'completed' ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                              Completed
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                              Voided
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-medium mb-2">No Reconciliations Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Start reconciling your bank accounts to ensure your books match your bank statements.
            </p>
            <Button onClick={() => navigate('/banking/reconcile/new')}>
              <Plus className="w-4 h-4 mr-2" />
              Start Your First Reconciliation
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
